import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'
import en from '../i18n/en.json'
import fr from '../i18n/fr.json'

const translations = { en, fr }

const LocaleContext = createContext()

function localeFromPath() {
  if (typeof window === 'undefined') return 'en'
  return window.location.pathname === '/fr' || window.location.pathname.startsWith('/fr/') ? 'fr' : 'en'
}

export function LocaleProvider({ children }) {
  const initialLocale = localeFromPath()
  const [locale, setLocale] = useState(initialLocale)
  const [t, setT] = useState(translations[initialLocale])

  // Function to detect locale from current path
  const detectLocale = () => localeFromPath()

  // Function to update locale and translations
  const updateLocale = (newLocale) => {
    setLocale(newLocale)
    setT(translations[newLocale])
    document.documentElement.lang = newLocale
  }

  useEffect(() => {
    // Initial locale detection
    const detectedLocale = detectLocale()
    updateLocale(detectedLocale)

    // Listen for popstate events (back/forward navigation)
    const handlePopState = () => {
      const newLocale = detectLocale()
      updateLocale(newLocale)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const changeLocale = (newLocale) => {
    if (newLocale === locale) return
    
    // Update URL first
    if (newLocale === 'fr') {
      window.history.pushState({}, '', '/fr')
    } else {
      window.history.pushState({}, '', '/')
    }
    
    // Then update locale and translations
    updateLocale(newLocale)
  }

  const setLocaleFromPath = useCallback((pathname) => {
    const next = pathname === '/fr' || pathname.startsWith('/fr/') ? 'fr' : 'en'
    updateLocale(next)
  }, [])

  const value = {
    locale,
    t,
    changeLocale,
    setLocaleFromPath
  }

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}
